import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Rocket, LineChart, ShieldCheck, Zap, Bell, KeyRound, X, Apple, Volume2, VolumeX, Download } from "lucide-react";
import { publicApi } from "@/lib/api";
import { getDeviceToken, todayUKDate } from "@/lib/device";
import { isPushSupported, subscribeToPush, pushEnabled } from "@/lib/push";
import { soundOn, setSoundOn, playChaChing } from "@/lib/sound";
import LockedTipCard from "@/components/LockedTipCard";
import TodaysExtraInfo from "@/components/TodaysExtraInfo";
import TrustTicker from "@/components/TrustTicker";
import YesterdayRecap from "@/components/YesterdayRecap";
import InstallGuide from "@/components/InstallGuide";
import {
  triggerInstall,
  canInstallDirectly,
  onInstallAvailabilityChange,
  isStandalone,
} from "@/lib/install";
import { toast } from "sonner";

export default function Home() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [showRestore, setShowRestore] = useState(false);
  const [restoreEmail, setRestoreEmail] = useState("");
  const [pushOn, setPushOn] = useState(pushEnabled());
  const [sound, setSound] = useState(soundOn());
  const [freeMode, setFreeMode] = useState(false);
  const [installOpen, setInstallOpen] = useState(false);
  const [canNativeInstall, setCanNativeInstall] = useState(canInstallDirectly());
  const deviceToken = getDeviceToken();

  useEffect(() => onInstallAvailabilityChange(setCanNativeInstall), []);

  const handleInstallClick = async () => {
    if (canInstallDirectly()) {
      const { outcome } = await triggerInstall();
      if (outcome === "accepted") {
        toast.success("Installing DailyRacer…");
        return;
      }
      if (outcome === "dismissed") return;
    }
    // Fallback: show the platform-specific guide
    setInstallOpen(true);
  };

  const load = async () => {
    try {
      const [d, cfg] = await Promise.all([
        publicApi.getTodayTips(deviceToken),
        publicApi.publicConfig().catch(() => ({ free_mode: false })),
      ]);
      setData(d);
      setFreeMode(!!cfg.free_mode);
    } catch (e) {
      toast.error("Couldn't load today's card");
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleUnlock = async () => {
    if (loading) return;
    setLoading(true);
    try {
      const payload = {
        device_token: deviceToken,
        race_date: data?.race_date || todayUKDate(),
        origin_url: window.location.origin,
        email: email || null,
      };
      const res = await publicApi.createCheckout(payload);
      window.location.href = res.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Checkout failed");
      setLoading(false);
    }
  };

  const handleEnablePush = async () => {
    try {
      await subscribeToPush();
      setPushOn(true);
      toast.success("Alerts on. You'll get today's tip drop.");
    } catch (e) {
      toast.error(e.message || "Couldn't enable alerts");
    }
  };

  const handleRestore = async () => {
    try {
      const r = await publicApi.restoreDevice({ email: restoreEmail, device_token: deviceToken });
      if (r.restored === 0) {
        toast.info("No unlocks found for that email");
      } else {
        toast.success(`Restored ${r.restored} unlock${r.restored === 1 ? "" : "s"}`);
        setShowRestore(false);
        setRestoreEmail("");
        await load();
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Restore failed");
    }
  };

  const unlocked = data?.unlocked;
  const tips = data?.tips || [];

  return (
    <div className="dr-shell pb-24">
      {/* Header */}
      <header className="dr-glass sticky top-0 z-20 flex items-center justify-between border-b px-6 py-4">
        <div>
          <div className="font-display text-3xl leading-none tracking-tighter text-white">
            DAILYRACER
          </div>
          <div className="mt-1 text-[10px] font-bold uppercase tracking-[0.28em] text-[#E1FF00]">
            Premium Horse Tips
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => {
              const next = !sound;
              setSoundOn(next);
              setSound(next);
              if (next) {
                playChaChing();
                toast.success("Sound on");
              } else {
                toast("Sound muted");
              }
            }}
            data-testid="sound-toggle-btn"
            aria-label="Toggle sound"
            className={`rounded-full border p-2 ${
              sound
                ? "border-[#E1FF00]/40 bg-[#E1FF00]/10 text-[#E1FF00]"
                : "border-zinc-800 text-zinc-500 hover:text-white"
            }`}
          >
            {sound ? <Volume2 size={13} /> : <VolumeX size={13} />}
          </button>
          {isPushSupported() && !pushOn && (
            <button
              onClick={handleEnablePush}
              data-testid="enable-push-btn"
              className="flex items-center gap-1 rounded-full border border-[#E1FF00]/40 bg-[#E1FF00]/10 px-3 py-1.5 text-[10px] font-bold uppercase tracking-widest text-[#E1FF00]"
            >
              <Bell size={12} /> Alerts
            </button>
          )}
          <Link
            to="/results"
            className="rounded-full border border-zinc-800 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-widest text-zinc-300 hover:bg-zinc-900"
            data-testid="nav-results"
          >
            Results
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="px-6 pt-8">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#E1FF00]">
            {new Date(data?.race_date || todayUKDate()).toLocaleDateString("en-GB", {
              weekday: "long",
              day: "2-digit",
              month: "short",
            })}
          </div>
          <h1 className="mt-2 text-center font-display text-[clamp(1.2rem,6.2vw,3rem)] leading-[1] tracking-tight text-white whitespace-nowrap">
            Best daily horse info
          </h1>
          <div className="mt-2 text-center font-display text-[clamp(1.8rem,9vw,3.5rem)] leading-none text-[#E1FF00]">
            for £2
          </div>
          <p className="mt-4 text-sm leading-relaxed text-zinc-400">
            Tap unlock. Apple Pay, Google Pay or card. £2 opens today's tip.
          </p>
          <div
            className="mt-4 block w-full rounded-2xl border border-[#E1FF00]/50 bg-[#E1FF00]/10 px-5 py-4 text-center"
            data-testid="promo-banner"
          >
            <div className="text-[11px] font-bold uppercase tracking-[0.22em] text-[#E1FF00]">
              Free tips for 5 days
            </div>
            <div className="mt-1 font-mono-data text-xs tracking-wider text-[#E1FF00]/80">
              29th Sept to 2nd October
            </div>
          </div>
        </motion.div>
      </section>

      {/* Tips list */}
      <div className="px-6">
        <YesterdayRecap />
      </div>

      {freeMode && (
        <section className="mt-4 px-6" data-testid="free-mode-banner">
          <button
            onClick={handleInstallClick}
            data-testid="how-to-install-card-top"
            className="w-full rounded-2xl border border-[#E1FF00]/50 bg-gradient-to-r from-[#E1FF00]/15 via-[#E1FF00]/5 to-[#E1FF00]/15 px-5 py-5 text-center transition hover:from-[#E1FF00]/20 hover:to-[#E1FF00]/20"
          >
            <div className="text-[10px] uppercase tracking-[0.28em] text-[#E1FF00]">
              How to download the app
            </div>
            <div className="mt-2 font-display text-lg leading-snug text-white">
              {isStandalone() ? (
                <>
                  You're all set — DailyRacer is on your home screen.<br />
                  <span className="text-zinc-400 text-sm">
                    Tap here to see the install steps you can share with mates.
                  </span>
                </>
              ) : (
                <>
                  Add DailyRacer to your home screen and activate alerts/bubbles<br />
                  <span className="text-zinc-400 text-sm">
                    so tips land instantly, every day.
                  </span>
                </>
              )}
            </div>
            <div className="mt-4 inline-flex items-center gap-2 rounded-full border border-[#E1FF00]/60 px-5 py-2.5 text-xs font-bold uppercase tracking-widest text-[#E1FF00]">
              <Download size={14} />
              {isStandalone()
                ? "See install steps"
                : canNativeInstall
                ? "Install DailyRacer"
                : "Show me how"}
            </div>
          </button>
        </section>
      )}

      <section className="mt-4 space-y-4 px-6" data-testid="tips-list">
        {tips.length === 0 && (
          <div className="rounded-2xl border border-dashed border-zinc-800 p-10 text-center">
            <div className="font-display text-2xl text-white">No tip live yet</div>
            <p className="mt-2 text-sm text-zinc-500">
              Today's selection drops shortly. Enable alerts so you don't miss it.
            </p>
          </div>
        )}
        {tips.map((t, i) => (
          <LockedTipCard key={t.id} tip={t} unlocked={unlocked} index={i} />
        ))}
      </section>

      {tips.length > 0 && !unlocked && !freeMode && (
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="mt-8 px-6"
        >
          <div className="rounded-2xl border border-zinc-800 bg-zinc-900 p-6">
            <div className="mb-4">
              <TrustTicker />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">
                One-tap unlock
              </div>
              <div className="mt-1 font-display text-4xl leading-none text-white">
                £2
              </div>
            </div>

            <input
              data-testid="recovery-email-input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Recovery email (optional)"
              className="mt-5 w-full rounded-full border border-zinc-800 bg-black px-4 py-3 text-sm text-white placeholder:text-zinc-600 focus:border-[#E1FF00]"
            />

            <button
              data-testid="unlock-tip-button"
              onClick={handleUnlock}
              disabled={loading}
              className="dr-btn-primary mt-4 flex w-full items-center justify-center gap-2 rounded-full px-6 py-4 text-base disabled:opacity-60"
            >
              <Rocket size={18} />
              {loading ? "Opening secure checkout…" : "Unlock today's tip £2"}
            </button>

            <div className="mt-4 flex items-center justify-center gap-2 text-[10px] uppercase tracking-[0.2em] text-zinc-500">
              <Apple size={12} className="text-zinc-400" /> Pay
              <span className="text-zinc-700">·</span>
              <span className="rounded bg-white/5 px-1.5 py-0.5 font-bold text-zinc-300">G Pay</span>
              <span className="text-zinc-700">·</span>
              Card
            </div>

            <div className="mt-4 grid grid-cols-2 gap-2 text-center text-[10px] uppercase tracking-[0.15em] text-zinc-500">
              <div className="flex flex-col items-center gap-1">
                <Zap size={14} className="text-zinc-400" /> Instant
              </div>
              <div className="flex flex-col items-center gap-1">
                <LineChart size={14} className="text-zinc-400" /> No sub
              </div>
            </div>
          </div>

          <button
            onClick={() => setShowRestore(true)}
            data-testid="restore-purchases-btn"
            className="mt-4 flex w-full items-center justify-center gap-2 text-xs uppercase tracking-[0.2em] text-zinc-500 hover:text-white"
          >
            <KeyRound size={12} /> Restore purchases
          </button>
        </motion.div>
      )}

      <TodaysExtraInfo />

      {/* Footer */}
      <footer className="mt-16 border-t border-zinc-900 px-6 py-12 text-center text-xs text-zinc-500">
        {!isStandalone() && (
          <button
            onClick={handleInstallClick}
            data-testid="footer-install-btn"
            className="mb-6 inline-flex items-center gap-2 rounded-full border border-zinc-700 px-4 py-2 text-[11px] font-bold uppercase tracking-widest text-zinc-200 hover:bg-zinc-900"
          >
            <Download size={12} /> {canNativeInstall ? "Install the app" : "How do I install the app?"}
          </button>
        )}
        <p>
          18+ · Gamble responsibly · Tips are opinion, not guaranteed. Never bet more than you can
          afford to lose.
        </p>
        <p className="mt-3 text-[10px] uppercase tracking-[0.24em] text-zinc-600">
          BeGambleAware.org · GamCare.org.uk
        </p>
        <Link
          to="/admin"
          className="mt-8 inline-block text-[10px] uppercase tracking-[0.2em] text-zinc-700 hover:text-zinc-500"
          data-testid="footer-admin-link"
        >
          Admin
        </Link>
      </footer>

      {/* Restore modal */}
      <AnimatePresence>
        {showRestore && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-40 flex items-end justify-center bg-black/70 backdrop-blur-sm sm:items-center"
            onClick={() => setShowRestore(false)}
            data-testid="restore-modal"
          >
            <motion.div
              initial={{ y: 40, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 40, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-md rounded-t-3xl border border-zinc-800 bg-zinc-950 p-6 sm:rounded-3xl"
            >
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">
                    Restore
                  </div>
                  <div className="font-display text-2xl text-white">Bring your unlocks back</div>
                </div>
                <button
                  onClick={() => setShowRestore(false)}
                  className="rounded-full border border-zinc-800 p-2 text-zinc-500 hover:text-white"
                  data-testid="restore-close-btn"
                >
                  <X size={14} />
                </button>
              </div>
              <p className="mt-3 text-sm text-zinc-400">
                Paste the email you used at checkout. We'll instantly restore any paid unlocks on
                this device.
              </p>
              <input
                data-testid="restore-email-input"
                type="email"
                value={restoreEmail}
                onChange={(e) => setRestoreEmail(e.target.value)}
                placeholder="you@example.com"
                className="mt-5 w-full rounded-full border border-zinc-800 bg-black px-4 py-3 text-sm text-white placeholder:text-zinc-600 focus:border-[#E1FF00]"
              />
              <button
                onClick={handleRestore}
                data-testid="restore-submit-btn"
                className="dr-btn-primary mt-3 w-full rounded-full px-6 py-3 text-sm"
              >
                Restore unlocks
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <InstallGuide open={installOpen} onClose={() => setInstallOpen(false)} />
    </div>
  );
}
